/**
 * Helper for implementing delivery date picker
 */
(function (deliveryDateUtil) {
  const DELIVERY_DAYS_BEFORE_EVENT_LIMIT = 5;
  deliveryDateUtil.DELIVERY_DAYS_BEFORE_EVENT_LIMIT =
    DELIVERY_DAYS_BEFORE_EVENT_LIMIT;

  deliveryDateUtil.deliveryDateInfo = {};

  /**
   * Validate a date with the following criteria
   *  - not within unavailable date ranges
   *  - within minimum and maximum date
   */
  const validateDate = (date, deliveryDateInfo) => {
    if (!deliveryDateInfo || !deliveryDateInfo.availabilities) {
      return false;
    }

    const { full, merged } = deliveryDateInfo.availabilities;

    // Format the date for comparison
    const dateStr = formatDateForComparison(date);

    // Check if date is in any of the unavailable ranges
    const isUnavailable = full.some((range) => {
      const fromDate = range.from;
      const toDate = range.to;
      const isInRange = dateStr >= fromDate && dateStr <= toDate;
      return isInRange;
    });

    // Get minimum and maximum dates
    const minDate = getMinDate(deliveryDateInfo);
    const maxDate = getMaxDateFromMinDate(minDate);
    const isWithinMinMax = date >= minDate && date <= maxDate;

    return !isUnavailable && isWithinMinMax;
  };

  /**
   * Format a date as MM-DD-YYYY for comparison
   */
  const formatDateForComparison = (date) => {
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  deliveryDateUtil.dateFormat = (inputDate, format) => {
    //parse the input date
    const date = new Date(inputDate);

    //extract the parts of the date
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    //replace the month
    format = format.replace("MM", month.toString().padStart(2, "0"));

    //replace the year
    if (format.indexOf("yyyy") > -1) {
      format = format.replace("yyyy", year.toString());
    } else if (format.indexOf("yy") > -1) {
      format = format.replace("yy", year.toString().substr(2, 2));
    }

    //replace the day
    format = format.replace("dd", day.toString().padStart(2, "0"));

    return format;
  };

  deliveryDateUtil.validateDate = validateDate;

  const isSaturdayDate = (date) => date.getDay() === 6;
  deliveryDateUtil.isSaturdayDate = isSaturdayDate;

  const validateWithinRange = (date, ranges) => {
    for (range of ranges) {
      const fromDate = new Date(new Date(range[0]).setHours(24, 0, 0, 0));
      const toDate = new Date(new Date(range[1]).setHours(24, 0, 0, 0));
      const compareDate = new Date(date.setHours(0, 0, 0, 0));

      if (compareDate >= fromDate && compareDate <= toDate) {
        return true;
      }
    }
    return false;
  };

  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };
  deliveryDateUtil.addDays = addDays;

  const subtractDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() - days);
    return result;
  };
  deliveryDateUtil.subtractDays = subtractDays;

  const getMinDate = (deliveryDateInfo) => {
    if (!deliveryDateInfo || !deliveryDateInfo.availabilities) {
      return new Date();
    }

    // Find processing time from messages
    const processingTimeRange = deliveryDateInfo.availabilities.full.find(
      (range) =>
        range.message &&
        range.message.includes("product needs") &&
        range.message.includes("days to deliver")
    );

    let processingDays = 7; // Default processing time
    if (processingTimeRange) {
      const match = processingTimeRange.message.match(/needs (\d+) days/);
      if (match && match[1]) {
        processingDays = parseInt(match[1], 10);
      }
    }

    // Calculate minimum date based on processing time
    const minDate = addDays(new Date(), processingDays);
    return minDate;
  };

  const getMaxDateFromMinDate = (minDate) => {
    if (!minDate) return;
    const maxDate = new Date(
      minDate.getFullYear() + 2,
      minDate.getMonth(),
      minDate.getDate()
    );
    return maxDate;
  };

  async function getAuthToken() {
    const base64 = btoa(
      `${window.username_data_endpoin_user}:${window.password_data_endpoint}`
    );
    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Basic ${base64}`,
      },
    };

    const rs = await fetch(`${window.url_data_endpoint_auth}`, options);
    if (rs.status === 200) {
      const res = await rs.json();
      return res.data.token;
    }

    console.error("Failed to get auth token");
    return undefined;
  }

  const getDeliveryDateInfo = async (productIds) => {
    const token = await getAuthToken();
    if (!token) {
      console.error("No auth token available");
      return { availabilities: { full: [], merged: [] } };
    }

    const hmac = window.hmac_key || "";
    const date = new Date();
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // JavaScript months are 0-indexed

    const payload = {
      product_ids: Array.isArray(productIds) ? productIds : [productIds],
      year: year,
      month: month,
      tz: "UTC",
      from: "delivery-date-utils",
    };

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-shopify-hmac-sha256": hmac,
      },
      body: JSON.stringify(payload),
    };

    const url = `${window.product_data_endpoint}/bundles/calendar`;
    const response = await fetch(url, options);
    if (!response.ok) {
      console.error(
        `Failed to fetch calendar data: ${response.status} ${response.statusText}`
      );
      return { availabilities: { full: [], merged: [] } };
    }

    const data = await response.json();
    return data;
  };
  deliveryDateUtil.getDeliveryDateInfo = getDeliveryDateInfo;

  /**
   * Fetch the delivery date info of the product using the handle. Then,
   * store the delivery date info as an item inside the global object.
   */
  const fetchDeliveryDateInfo = async (handle) => {
    if (!deliveryDateUtil.deliveryDateInfo[handle]) {
      deliveryDateUtil.deliveryDateInfo[handle] = { state: "loading" };

      try {
        const response = await getDeliveryDateInfo(handle);

        if (response.status === "success" && response.data) {
          deliveryDateUtil.deliveryDateInfo[handle] = {
            state: "success",
            data: response.data,
          };
        } else {
          console.error(
            "Failed to get delivery date info:",
            response.msg || "Unknown error"
          );
          deliveryDateUtil.deliveryDateInfo[handle] = { state: "failed" };
        }
      } catch (error) {
        console.error("Error fetching delivery date info:", error);
        deliveryDateUtil.deliveryDateInfo[handle] = { state: "failed" };
      }
    }
  };

  const enableInputElement = (inputElem, selectMsg) => {
    inputElem
      .prop("disabled", false)
      .prop("placeholder", selectMsg)
      .removeClass("disabled");
  };

  function mobileCheck() {
    let check = false;
    // prettier-ignore
    (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
    return check;
  }

  /**
   * Set the default as the best delivery date before the event date.
   *
   * @returns {boolean} true if event date doesn't exist or event date exists
   * and is within the available range. false if available ranges doesn't match
   * the event date.
   */
  const setDefaultDeliveryDate = (handle, customerId, inputElem) => {
    const deliveryDateInfo = deliveryDateUtil.deliveryDateInfo[handle].data;
    if (!deliveryDateInfo) {
      return null;
    }

    // Default best delivery day is 3 days before event
    const bestDeliveredBefore = 3;
    const eventDateStr = localStorage.getItem(`event-date-${customerId}`);
    if (!eventDateStr) {
      return true;
    }

    const eventDate = new Date(eventDateStr);
    const optimalDate = subtractDays(eventDate, bestDeliveredBefore);

    // Check few days before the optimal date
    for (let i = 0; i < DELIVERY_DAYS_BEFORE_EVENT_LIMIT; i++) {
      const suggestedDate = subtractDays(optimalDate, i);

      if (
        validateDate(suggestedDate, deliveryDateInfo) &&
        !isSaturdayDate(suggestedDate)
      ) {
        inputElem.val($.datepicker.formatDate("mm/dd/yy", suggestedDate));
        inputElem.change();
        return true;
      }
    }

    // Check days after the optimal date up to a day before the event day
    for (let i = 1; i < bestDeliveredBefore; i++) {
      const suggestedDate = addDays(optimalDate, i);

      if (validateDate(suggestedDate, deliveryDateInfo)) {
        inputElem.val($.datepicker.formatDate("mm/dd/yy", suggestedDate));
        inputElem.change();
        return true;
      }
    }

    //if there's no optimal day before or after, set the first one available before
    for (let i = 0; i < DELIVERY_DAYS_BEFORE_EVENT_LIMIT; i++) {
      const suggestedDate = subtractDays(optimalDate, i);
      if (!isSaturdayDate(suggestedDate)) {
        inputElem.val($.datepicker.formatDate("mm/dd/yy", suggestedDate));
        inputElem.change();
        return false;
      }
    }

    return false;
  };

  deliveryDateUtil.formatRecommendedDate = (date) => {
    const formatted = date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    return formatted;
  };

  /**
   * Fetch available delivery day for the product and store it.
   * Then, attach a date picker.
   */
  deliveryDateUtil.datepicker = async (
    selector,
    handle,
    options = {
      loadingMsg: "Loading Delivery Date...",
      selectMsg: "Please select a valid delivery date",
    },
    customerId,
    onShowBestDeliveryDay,
    onShowWarningMessage
  ) => {
    // Mobile detection
    const inputElem = $(selector);

    if (mobileCheck()) {
      inputElem.prop("readonly", true);
    }

    // Fetch delivery date info and parse props
    await fetchDeliveryDateInfo(handle);

    const selectedDeliveryDateInfo =
      deliveryDateUtil.deliveryDateInfo[handle].data;

    const minDate = getMinDate(selectedDeliveryDateInfo);
    const maxDate = getMaxDateFromMinDate(minDate);

    // Render date picker
    inputElem.datepicker({
      minDate: minDate,
      maxDate: maxDate,
      changeMonth: true,
      changeYear: true,
      beforeShowDay: (date) => {
        const isValid = validateDate(
          date,
          deliveryDateUtil.deliveryDateInfo[handle].data
        );
        return [isValid];
      },
    });

    // Render input field
    enableInputElement(inputElem, options.selectMsg);

    const isEventDateWithinAvailableRange = setDefaultDeliveryDate(
      handle,
      customerId,
      inputElem
    );

    if (isEventDateWithinAvailableRange) {
      onShowBestDeliveryDay && onShowBestDeliveryDay(handle);
    } else {
      onShowWarningMessage && onShowWarningMessage();
    }
  };

  /**
   * Validate delivery date string from input. A dateStr must be
   *  - not empty
   *  - in valid format
   *  - one of the available dates
   *  - not black-out dates
   *
   * @param {string} dateStr date string to validate
   */
  deliveryDateUtil.validateDateStr = (dateStr, handle) => {
    if (dateStr.length === 0) {
      return false;
    }

    try {
      $.datepicker.parseDate("mm/dd/yy", dateStr);
    } catch (e) {
      console.error("Invalid date format:", e);
      return false;
    }

    const date = new Date(dateStr);

    const result = validateDate(
      date,
      deliveryDateUtil.deliveryDateInfo[handle].data
    );
    return result;
  };
})((window.deliveryDateUtil = window.deliveryDateUtil || {}));
